import { useEffect, useMemo, useRef, useContext } from 'react';
import { useIntercom } from 'react-use-intercom';
import { SplitFactoryProvider } from '@splitsoftware/splitio-react';
import { Navigate, useLocation, useParams } from 'react-router';
import queryString from 'query-string';
import { Flex } from '@chakra-ui/react';
import { UserAccount } from '@webapp/bff/src/types/user';
import { Outlet } from 'react-router-dom';
import useCurrentUserData from '../../../data/useCurrentUserData';
import ErrorPage from '../../../components/ErrorPage';
import { pathLogin, pathMfaLogin, pathCookies } from '../../../constants/path';
import {
  DataHandlerFeedback,
  hasDataHandlerFeedBack,
} from '../../../utils/dataHandler';
import {
  AnalyticsPageViewTracker,
  UserAnalyticsProvider,
} from '../../../hooks/analytics/useAnalytics';
import { AuthContext } from './types';
import { envConfig } from '../../../config/envConfig';

function cookiesEnabled() {
  let { cookieEnabled } = navigator;
  if (!cookieEnabled) {
    document.cookie = 'testcookie';
    cookieEnabled = document.cookie.indexOf('testcookie') !== -1;
  }
  return cookieEnabled;
}

const convertAccountStatusToSessionState = (
  accountStatus: UserAccount['accountStatus']
):
  | 'onboarding:invited'
  | 'onboarding:missing_phone_number'
  | 'onboarding:waiting_phone_confirmation'
  | 'waiting_mfa_code'
  | 'invalid'
  | 'active' => {
  switch (accountStatus) {
    case 'ACTIVE':
      return 'active';
    case 'INVITED':
      return 'onboarding:invited';
    case 'MISSING_PHONE_NUMBER':
      return 'onboarding:missing_phone_number';
    case 'MISSING_MFA':
      return 'waiting_mfa_code';
    case 'INVALID':
      return 'invalid';
    default:
      throw new Error('Invalid account status');
  }
};

export default function ContextAuth({
  userRequired = true,
  onboarding = false,
}: {
  userRequired?: boolean;
  onboarding?: boolean;
}) {
  const { token: tokenFromParams } = useParams();
  const { search, pathname } = useLocation();
  const { token: tokenFromQuery } = queryString.parse(search);
  const token = resolveTokenFromUrl(tokenFromParams, tokenFromQuery);
  const userDataHandler = useCurrentUserData(
    userRequired
      ? `auth-context-user-data-handler-${localStorage.getItem('session-id')}-onboarding-${onboarding}`
      : null,
    token
  );
  const { data: user } = userDataHandler;
  const contextValue = useMemo(
    () => ({
      user: user
        ? {
            legacyUserId: user.userId,
            userId: user.externalId,
            email: user.email,
            name: user.name,
            firstName: user.firstName,
            lastName: user.lastName,
            isAdmin: user.roles.includes('ROLE_ADMIN_USER'),
            customerData: user.customerData,
            sessionState: convertAccountStatusToSessionState(
              user.accountStatus
            ),
            orgAccess: {
              canEdit: user.orgAccess.canEdit ?? [],
              canEditAll: user.orgAccess.canEditAll ?? false,
            },
          }
        : null,
    }),
    [user]
  );
  const { boot } = useIntercom();
  const intercomInitRef = useRef('');
  const currentUserHash = JSON.stringify({
    id: user?.userId,
    email: user?.email,
    firstName: user?.firstName,
    lastName: user?.lastName,
    status: user?.accountStatus,
  });
  useEffect(() => {
    if (intercomInitRef.current !== currentUserHash) {
      if (!user && !userRequired) {
        intercomInitRef.current = currentUserHash;
        boot();
      } else if (user) {
        boot({
          userId: user.userId.toString(),
          email: user.email,
          name: `${user.firstName} ${user.lastName}`,
          customLauncherSelector: '#intercom-activator',
        });
        intercomInitRef.current = currentUserHash;
      }
    }
  }, [user, currentUserHash, boot, userRequired]);
  const splitConfig = useMemo(
    () => ({
      core: {
        authorizationKey: envConfig.SPLIT_CLIENT_API_KEY,
        key: `${user?.email ?? 'no-user@mooven.com'}`,
      },
    }),
    [user?.email]
  );
  if (!cookiesEnabled()) {
    return <Navigate to={pathCookies()} />;
  }
  if (hasDataHandlerFeedBack([userDataHandler])) {
    return (
      <Flex flex="1" alignItems="center" justifyContent="center">
        <DataHandlerFeedback dataHandlersParam={[userDataHandler]} />
      </Flex>
    );
  }
  if (contextValue.user?.sessionState === 'invalid') {
    return (
      <ErrorPage text="The user is not allowed to access this application, please contact support" />
    );
  }
  if (userRequired && !user) {
    return <Navigate to={pathLogin()} />;
  }

  switch (contextValue.user?.sessionState) {
    case 'onboarding:invited':
    case 'onboarding:missing_phone_number':
    case 'onboarding:waiting_phone_confirmation':
      if (!onboarding) {
        return <Navigate to="/onboarding/create-account" />;
      }
      break;
    case 'waiting_mfa_code':
      if (!onboarding && pathname !== pathMfaLogin()) {
        return <Navigate to={pathMfaLogin()} />;
      }
      break;
    default:
      break;
  }
  return (
    <SplitFactoryProvider config={splitConfig}>
      <AuthContext.Provider value={contextValue}>
        <UserAnalyticsProvider>
          <AnalyticsPageViewTracker />
          <Outlet />
        </UserAnalyticsProvider>
      </AuthContext.Provider>
    </SplitFactoryProvider>
  );
}

export function useContextAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(
      'useAuthContext must be used within a AuthContext.Provider'
    );
  }
  return context;
}

function resolveTokenFromUrl(
  tokenFromParams?: unknown,
  tokenFromQuery?: unknown
) {
  if (typeof tokenFromParams === 'string') {
    return tokenFromParams;
  }
  if (typeof tokenFromQuery === 'string') {
    return tokenFromQuery;
  }
  return undefined;
}
